import { toast } from "sonner";

export interface AIError {
  status: number;
  message: string;
  userMessage: string;
  action?: string;
  actionLabel?: string;
}

export const AI_ERROR_MESSAGES: Record<number, AIError> = {
  429: {
    status: 429,
    message: "Rate limit exceeded",
    userMessage: "Vous avez atteint la limite de requêtes IA. Veuillez patienter quelques instants avant de réessayer.",
    action: "Réessayez dans 30 secondes ou passez à un plan supérieur pour augmenter vos limites.",
    actionLabel: "Voir les plans",
  },
  402: {
    status: 402,
    message: "Payment required",
    userMessage: "Votre quota IA mensuel est épuisé.",
    action: "Ajoutez des crédits à votre compte pour continuer à utiliser les fonctionnalités IA.",
    actionLabel: "Ajouter des crédits",
  },
  500: {
    status: 500,
    message: "Server error",
    userMessage: "Une erreur s'est produite lors du traitement IA.",
    action: "Réessayez dans quelques instants. Si le problème persiste, contactez le support.",
  },
};

export function handleAIError(error: any, context?: string): AIError | null {
  // Check if it's a fetch response with status
  if (error?.status && AI_ERROR_MESSAGES[error.status]) {
    return AI_ERROR_MESSAGES[error.status];
  }

  // Check error message for specific patterns
  const errorMessage = error?.message || error?.error || String(error);
  
  if (errorMessage.includes("Rate limit") || errorMessage.includes("429")) {
    return AI_ERROR_MESSAGES[429];
  }
  
  if (errorMessage.includes("Payment required") || errorMessage.includes("402")) {
    return AI_ERROR_MESSAGES[402];
  }

  // Generic error
  return {
    status: 500,
    message: errorMessage,
    userMessage: context 
      ? `Erreur lors de ${context}: ${errorMessage}`
      : `Une erreur s'est produite: ${errorMessage}`,
    action: "Veuillez réessayer. Si le problème persiste, contactez le support.",
  };
}

export function showAIErrorToast(error: any, context?: string) {
  const aiError = handleAIError(error, context);
  
  if (!aiError) return;

  const toastOptions: any = {
    description: aiError.action,
    duration: aiError.status === 429 ? 10000 : 8000,
  };

  if (aiError.status === 429) {
    toast.warning(aiError.userMessage, toastOptions);
  } else if (aiError.status === 402) {
    toast.error(aiError.userMessage, {
      ...toastOptions,
      action: aiError.actionLabel ? {
        label: aiError.actionLabel,
        onClick: () => window.location.href = "/settings",
      } : undefined,
    });
  } else {
    toast.error(aiError.userMessage, toastOptions);
  }

  return aiError;
}

export async function handleFetchWithAIErrors(
  response: Response,
  context?: string
): Promise<{ ok: boolean; data?: any; error?: AIError }> {
  if (response.ok) {
    const data = await response.json();
    return { ok: true, data };
  }

  // Handle specific AI error codes
  if (response.status === 429 || response.status === 402) {
    const aiError = AI_ERROR_MESSAGES[response.status];
    showAIErrorToast({ status: response.status }, context);
    return { ok: false, error: aiError };
  }

  // Try to parse error message from response
  try {
    const errorData = await response.json();
    const aiError = handleAIError(
      { status: response.status, message: errorData.error || errorData.message },
      context
    );
    if (aiError) {
      showAIErrorToast(aiError, context);
      return { ok: false, error: aiError };
    }
  } catch {
    // Ignore JSON parse errors
  }

  return { ok: false, error: AI_ERROR_MESSAGES[500] };
}
